import styled, { css } from 'styled-components'
import { colors } from '@/styles'
import { Label } from '@/components/text'
import Tooltip from '@/components/popper-tooltip'
import Icon from '@/components/icon'

export type StatusBadgeType =
  | 'error'
  | 'success'
  | 'default'
  | 'warning'
  | 'info'
  | 'blue'
  | 'pending'
  | 'link'
  | 'poor'
  | 'average'
  | 'very_good'
  | 'excellent'
  | 'analyzing'

type Color = 'primary' | 'secondary'

type StatusBadgeProps = {
  type: StatusBadgeType
  size?: 'small' | 'medium'
  space?: 'fit' | 'full'
  title: string
  tooltip?: React.ReactNode
  tooltipTheme?: Color
  toolTipIcon?: boolean
  isLocked?: boolean
}

type StyleProps = Omit<StatusBadgeProps, 'title' | 'tooltip'>

const blue = css`
  background-color: ${colors.blue96};
  color: ${colors.blue16};
  svg {
    path {
      fill: ${colors.blue16};
    }
  }
`

const error = css`
  background-color: ${colors.red96};
  color: ${colors.red64};
  svg {
    path {
      fill: ${colors.red64};
    }
  }
`

const poor = css`
  background-color: ${colors.red40};
  color: ${colors.white};
`

const average = css`
  background-color: ${colors.yellow48};
  color: ${colors.white};
`

const veryGood = css`
  background-color: ${colors.veryGood};
  color: ${colors.white};
`

const excellent = css`
  background-color: ${colors.green48};
  color: ${colors.white};
`
const analyzing = css`
  background-color: ${colors.grey32};
  color: ${colors.white};
`

const success = css`
  background-color: #ebfffb;
  color: #00a38b;
  svg {
    path {
      fill: #00a38b;
    }
  }
`

const info = css`
  background-color: ${colors.purple96};
  color: ${colors.purple40};
  svg {
    path {
      fill: ${colors.purple40};
    }
  }
`

const warning = css`
  background-color: #fffbd8;
  color: #8c7317;
  svg {
    path {
      fill: #8c7317;
    }
  }
`

const pending = css`
  background-color: ${colors.yellow96};
  color: ${colors.yellow24};
  svg {
    path {
      fill: ${colors.yellow24};
    }
  }
`

const full = css`
  width: 100%;
  div {
    width: 100%;
    text-align: center;
  }
`

const link = css`
  color: ${colors.link};
  background-color: ${colors.white};

  div {
    width: 100%;
    text-align: left;
  }
`

const getTypeStyles = (props: StyleProps) => {
  if (props.type === 'error') return error
  if (props.type === 'success') return success
  if (props.type === 'info') return info
  if (props.type === 'warning') return warning
  if (props.type === 'blue') return blue
  if (props.type === 'pending') return pending
  if (props.type === 'link') return link
  if (props.type === 'poor') return poor
  if (props.type === 'average') return average
  if (props.type === 'very_good') return veryGood
  if (props.type === 'excellent') return excellent
  if (props.type === 'analyzing') return analyzing

  return
}

const getSizeStyles = (size: StyleProps['size']) => {
  if (size === 'small') return '12px'
  return '14px'
}

const getSpaceStyles = (space: StyleProps['space']) => {
  if (space === 'full') return full
}

const Wrapper = styled.div<StyleProps>`
  display: flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  width: fit-content;
  height: 24px;
  gap: 0 8px;
  background-color: ${colors.grey96};
  color: ${colors.grey56};
  svg {
    margin-bottom: 2px;
    path {
      fill: ${colors.grey56};
    }
  }
  ${props => getTypeStyles(props)};
  ${props => getSpaceStyles(props.space)};
  ${props =>
    props.isLocked &&
    `
    filter: blur(10px);
    color: ${colors.black};
    background-color: ${colors.grey96};
    -webkit-touch-callout: none;
    -webkit-user-select: none; 
    -khtml-user-select: none; 
    -moz-user-select: none; 
    -ms-user-select: none; 
    user-select: none; 
    cursor: not-allowed;
  `}
`

const StatusBadge: React.FC<StatusBadgeProps> = ({
  type = 'default',
  size = 'medium',
  space = 'fit',
  title,
  toolTipIcon,
  tooltip,
  tooltipTheme,
  isLocked = false
}) => {
  return tooltip ? (
    <Tooltip content={tooltip} theme={tooltipTheme}>
      <Wrapper type={type} isLocked={isLocked}>
        {toolTipIcon && <Icon id="info_blue2" width={15} height={15} />}
        <Label fontSize={getSizeStyles(size)}>{title}</Label>
      </Wrapper>
    </Tooltip>
  ) : (
    <Wrapper type={type} space={space} isLocked={isLocked}>
      <Label fontSize={getSizeStyles(size)}>{title}</Label>
    </Wrapper>
  )
}

export default StatusBadge
